/**
 * RoleEditor - shared component for creating + editing custom roles.
 *
 * Modes:
 *   - mode="create" - render visibility radio (super only) +
 *     PartnerPicker + name + description + "Assignable in" allowlist
 *     + "Start from" pre-fill picker + permission grid; submit calls
 *     ``apiClient.createRole``.
 *   - mode="edit"   - partner is fixed (read-only display); name +
 *     description + "Assignable in" allowlist + grid; submit calls
 *     ``apiClient.patchRole``. Visibility tier + "Start from" are
 *     create-only.
 *
 * Permission grid layout: rows grouped by category (transaction /
 * payment / metrics / inventory / …), each row a checkbox +
 * (when ``supports_scope=true``) a scope dropdown. Scope dropdown
 * options come from each permission's ``valid_scopes`` so a role
 * can't carry a scope the permission doesn't support.
 *
 * State model: ``selected`` is a Map<permission_code, scope|null>.
 * Toggle adds / removes the entry; scope change updates it. The
 * submit handler turns the map into the array shape the API takes.
 *
 * V0074 visibility tiers (create only):
 *   - "Partner-scoped" (default; visible to one partner subtree)
 *   - "Platform-wide" (super_admin only; visible to every partner;
 *     submits with partner_id=null)
 */

import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, ShieldCheck, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import type {
  PermissionDef,
  RoleDetailResponse,
  RoleGrantInput,
  RolePermissionGrant,
} from "@tilt/admin-sdk";

import { PartnerPicker } from "@/components/PartnerPicker";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { isSuperAdmin } from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";

type RoleEditorMode =
  | { readonly kind: "create" }
  | {
      readonly kind: "edit";
      readonly roleId: string;
      /** Empty string for V0074 platform-custom rows (partner_id=null). */
      readonly partnerId: string;
      readonly partnerName: string | null;
      readonly partnerPath: string | null;
      readonly initialName: string;
      readonly initialDescription: string | null;
      readonly initialGrants: ReadonlyArray<RolePermissionGrant>;
      /** V0074 - null/empty means "anyone who can see can assign". */
      readonly initialAssignablePaths: ReadonlyArray<string> | null;
    }
  | {
      /** Grants-only patch for system roles - name/description/assignable
       *  remain untouched; only the permission grid is shown. */
      readonly kind: "system-grants";
      readonly roleId: string;
      readonly roleName: string;
      readonly initialGrants: ReadonlyArray<RolePermissionGrant>;
    };

type VisibilityTier = "partner" | "platform";

export function RoleEditor(props: {
  readonly mode: RoleEditorMode;
  readonly onCancel?: () => void;
  /** Called after a successful create / patch - typically the host
   *  page closes the editor or navigates. The fresh role payload is
   *  passed so the host can update its cached detail. */
  readonly onSaved?: (role: RoleDetailResponse) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const profile = useRoleProfile();
  const callerIsSuper = isSuperAdmin(profile);

  const permissions = useQuery({
    queryKey: ["permissions", "list"],
    queryFn: () => apiClient.listPermissions(),
    staleTime: 5 * 60_000,
  });

  // Whole role list - used by the "Start from" picker on create mode
  // to pre-fill grants. Cheap: same query the catalog page uses, so
  // the cache slot is shared.
  const roleList = useQuery({
    queryKey: ["roles", "list"],
    queryFn: () => apiClient.listRoles({ include_system: true }),
    staleTime: 60_000,
    enabled: props.mode.kind === "create",
  });

  // Local form state.
  const [visibility, setVisibility] = useState<VisibilityTier>(() => {
    if (props.mode.kind === "edit") {
      return props.mode.partnerId ? "partner" : "platform";
    }
    return "partner";
  });
  const [partnerId, setPartnerId] = useState<string>(
    props.mode.kind === "edit" ? props.mode.partnerId : "",
  );
  const [name, setName] = useState<string>(
    props.mode.kind === "edit" ? props.mode.initialName : "",
  );
  const [description, setDescription] = useState<string>(
    props.mode.kind === "edit" ? (props.mode.initialDescription ?? "") : "",
  );
  // V0074 - partner_path strings (NOT IDs). Empty array = anyone-can-assign.
  const [assignablePaths, setAssignablePaths] = useState<readonly string[]>(() => {
    if (props.mode.kind !== "edit") return [];
    return props.mode.initialAssignablePaths ?? [];
  });
  // V0074 (create-only) - UUIDs of roles whose grants we already merged in.
  const [seedRoleIds, setSeedRoleIds] = useState<readonly string[]>([]);
  // selected: permission_code -> chosen scope (or null when the
  // permission's ``supports_scope`` is false).
  const [selected, setSelected] = useState<Map<string, string | null>>(() => {
    const grants =
      props.mode.kind === "edit" || props.mode.kind === "system-grants"
        ? props.mode.initialGrants
        : null;
    if (!grants) return new Map();
    const m = new Map<string, string | null>();
    for (const g of grants) {
      m.set(g.permission_code, g.scope);
    }
    return m;
  });
  const [error, setError] = useState<string | null>(null);

  const grouped = useMemo(
    () => groupByCategory(permissions.data?.permissions ?? []),
    [permissions.data],
  );

  // Build the request payload's partner_id field once, so the radio
  // and the picker both feed the same submit logic.
  const submitPartnerId: string | null =
    props.mode.kind === "create" ? (visibility === "platform" ? null : partnerId) : null; // patch never touches partner_id

  // Build the request payload's assignable_to_partner_paths field.
  // Empty array → null (anyone-can-assign).
  const submitAssignable: string[] | null =
    assignablePaths.length === 0 ? null : [...assignablePaths];

  // Mutations.
  const createMut = useMutation({
    mutationFn: () =>
      apiClient.createRole({
        // Conditional spread: omit ``partner_id`` for platform-custom
        // (the SDK type allows undefined) - the backend reads
        // missing-or-null identically and stamps partner_id=NULL.
        ...(submitPartnerId ? { partner_id: submitPartnerId } : { partner_id: null }),
        name: name.trim(),
        description: description.trim() || null,
        grants: serialiseGrants(selected),
        assignable_to_partner_paths: submitAssignable,
      }),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["roles", "list"] });
      qc.invalidateQueries({ queryKey: ["roles", "detail", resp.role.id] });
      if (props.onSaved) {
        props.onSaved(resp);
      } else {
        navigate(`/platform/roles/${resp.role.id}`);
      }
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const patchMut = useMutation({
    mutationFn: () => {
      if (props.mode.kind === "system-grants") {
        return apiClient.patchRole(props.mode.roleId, {
          grants: serialiseGrants(selected),
        });
      }
      if (props.mode.kind !== "edit") throw new Error("not in edit mode");
      return apiClient.patchRole(props.mode.roleId, {
        name: name.trim(),
        description: description.trim() || null,
        grants: serialiseGrants(selected),
        assignable_to_partner_paths: submitAssignable,
      });
    },
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["roles", "list"] });
      qc.invalidateQueries({ queryKey: ["roles", "detail", resp.role.id] });
      if (props.onSaved) props.onSaved(resp);
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const isSubmitting = createMut.isPending || patchMut.isPending;
  const submitDisabled =
    isSubmitting ||
    (props.mode.kind !== "system-grants" &&
      (!name.trim() || (props.mode.kind === "create" && visibility === "partner" && !partnerId)));

  function handleSubmit(): void {
    setError(null);
    if (props.mode.kind === "create") {
      createMut.mutate();
    } else {
      patchMut.mutate();
    }
  }

  function toggle(perm: PermissionDef): void {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(perm.code)) {
        next.delete(perm.code);
      } else {
        next.set(perm.code, perm.supports_scope ? perm.default_scope : null);
      }
      return next;
    });
  }

  function changeScope(code: string, scope: string): void {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(code)) next.set(code, scope);
      return next;
    });
  }

  // ---------------------------------------------------------------- //
  // V0074 - "Assignable in" allowlist handlers.
  // Stored as partner_path strings; the UI lookups the partner row to
  // render a friendly name on each chip.
  // ---------------------------------------------------------------- //
  const partnersList = useQuery({
    queryKey: ["partners", "all"],
    queryFn: () => apiClient.listPartners({ limit: 200 }),
    staleTime: 60_000,
  });
  const partnerByPath = useMemo(() => {
    const m = new Map<string, { id: string; name: string; path: string }>();
    for (const p of partnersList.data?.partners ?? []) {
      m.set(p.path, { id: p.id, name: p.name, path: p.path });
    }
    return m;
  }, [partnersList.data]);

  function addAssignablePath(partnerIdToAdd: string): void {
    if (!partnerIdToAdd) return;
    const partner = (partnersList.data?.partners ?? []).find((p) => p.id === partnerIdToAdd);
    if (!partner) return;
    if (assignablePaths.includes(partner.path)) return;
    setAssignablePaths((prev) => [...prev, partner.path]);
  }

  function removeAssignablePath(pathToRemove: string): void {
    setAssignablePaths((prev) => prev.filter((p) => p !== pathToRemove));
  }

  // ---------------------------------------------------------------- //
  // V0074 - "Start from" pre-fill (create-only). Selecting a role
  // detail-fetches its grants and merges into the local ``selected``
  // map. De-selecting a previously seeded role does NOT roll back -
  // the user can manually clean up. (Documented helper-text below.)
  // ---------------------------------------------------------------- //
  const seedDetailQueries = useQueries({
    queries: seedRoleIds.map((rid) => ({
      queryKey: ["roles", "detail", rid],
      queryFn: () => apiClient.getRole(rid),
      staleTime: 60_000,
      enabled: props.mode.kind === "create",
    })),
  });

  // When a fresh seed-role's detail arrives, merge its grants in.
  // Tracked by id so we don't double-merge on re-render. Use a ref-
  // like Set kept in state to avoid leaking memory across renders.
  const [mergedSeedIds, setMergedSeedIds] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (props.mode.kind !== "create") return;
    seedDetailQueries.forEach((q, i) => {
      const rid = seedRoleIds[i];
      if (!rid) return;
      if (mergedSeedIds.has(rid)) return;
      if (!q.data) return;
      // Merge grants (dedupe by permission_code; later seed never
      // clobbers an earlier choice, including manual edits the user
      // has already made).
      setSelected((prev) => {
        const next = new Map(prev);
        for (const g of q.data.grants) {
          if (!next.has(g.permission_code)) {
            next.set(g.permission_code, g.scope);
          }
        }
        return next;
      });
      setMergedSeedIds((prev) => {
        const s = new Set(prev);
        s.add(rid);
        return s;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedDetailQueries.map((q) => q.dataUpdatedAt).join(","), seedRoleIds.join(",")]);

  function toggleSeedRole(roleId: string): void {
    setSeedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId],
    );
  }

  // Auto-clear error when user edits the form.
  useEffect(() => {
    setError(null);
  }, [name, description, partnerId, visibility, selected, assignablePaths]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-600" aria-hidden="true" />
          <h2 className="text-lg font-semibold tracking-tight">
            {props.mode.kind === "create"
              ? "New custom role"
              : props.mode.kind === "system-grants"
                ? `Edit permissions - ${props.mode.roleName}`
                : "Edit role"}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {props.onCancel && (
            <button
              type="button"
              onClick={props.onCancel}
              disabled={isSubmitting}
              className="rounded-md border bg-background px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitDisabled}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {props.mode.kind === "create" ? "Create role" : "Save"}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">{error}</div>
      )}

      {/* Identity - hidden in system-grants mode (name/description are migration-managed). */}
      {props.mode.kind !== "system-grants" && (
        <section className="space-y-3 rounded-lg border bg-card p-4">
          {/* V0074 - visibility tier (create-only, super_admin-only). */}
          {props.mode.kind === "create" && callerIsSuper && (
            <fieldset>
              <legend className="mb-1 text-xs font-medium text-muted-foreground">Visibility</legend>
              <div className="flex flex-wrap gap-3">
                <label className="inline-flex items-center gap-1.5 text-xs">
                  <input
                    type="radio"
                    name="role-visibility"
                    value="partner"
                    checked={visibility === "partner"}
                    onChange={() => setVisibility("partner")}
                    className="h-3.5 w-3.5"
                  />
                  <span>Partner-scoped</span>
                </label>
                <label className="inline-flex items-center gap-1.5 text-xs">
                  <input
                    type="radio"
                    name="role-visibility"
                    value="platform"
                    checked={visibility === "platform"}
                    onChange={() => setVisibility("platform")}
                    className="h-3.5 w-3.5"
                  />
                  <span>Platform-wide</span>
                </label>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Platform-wide roles are visible to every partner. Partner-scoped roles live under
                one partner subtree.
              </p>
            </fieldset>
          )}

          {props.mode.kind === "create" ? (
            visibility === "partner" ? (
              <div>
                <p className="mb-1 text-xs font-medium text-muted-foreground">
                  Workplace (partner)
                </p>
                <PartnerPicker value={partnerId} onChange={(v) => setPartnerId(v)} />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  The role lives under this partner. Their org / location memberships use it.
                </p>
              </div>
            ) : (
              <div>
                <p className="text-xs font-medium text-muted-foreground">Workplace</p>
                <p className="text-sm text-foreground">Platform (visible to every partner)</p>
              </div>
            )
          ) : (
            <div>
              <p className="text-xs font-medium text-muted-foreground">Workplace (partner)</p>
              <p className="text-sm text-foreground">
                {props.mode.partnerId
                  ? (props.mode.partnerName ?? "-")
                  : "Platform (visible to every partner)"}
                {props.mode.partnerPath && (
                  <span className="ml-1 font-mono text-[11px] text-muted-foreground">
                    · {props.mode.partnerPath}
                  </span>
                )}
              </p>
            </div>
          )}
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Night Shift Cashier"
              className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">
              Description (optional)
            </span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="What this role is for. Operators see this on the People page."
              className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
            />
          </label>

          {/* V0074 - Assignable-in allowlist. */}
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              Assignable in (optional)
            </p>
            <div className="space-y-2">
              {assignablePaths.length > 0 && (
                <ul className="flex flex-wrap gap-1.5">
                  {assignablePaths.map((p) => {
                    const partner = partnerByPath.get(p);
                    return (
                      <li
                        key={p}
                        className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-2 py-0.5 text-xs"
                      >
                        <span>{partner?.name ?? p}</span>
                        <span className="font-mono text-[11px] text-muted-foreground">{p}</span>
                        <button
                          type="button"
                          onClick={() => removeAssignablePath(p)}
                          className="ml-0.5 rounded p-0.5 hover:bg-muted"
                          aria-label={`Remove ${partner?.name ?? p}`}
                        >
                          <X className="h-3 w-3" aria-hidden="true" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <PartnerPicker
                value=""
                onChange={(id) => {
                  addAssignablePath(id);
                }}
                emptyLabel="Add a partner…"
              />
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Leave empty to allow anyone who can see this role to assign it. Pick partners to
              restrict assignment to those subtrees only.
            </p>
          </div>

          {/* V0074 - Start from (create-only). */}
          {props.mode.kind === "create" && (
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">
                Start from (optional)
              </p>
              {roleList.isLoading ? (
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading roles…
                </p>
              ) : (
                <div className="space-y-2">
                  {seedRoleIds.length > 0 && (
                    <ul className="flex flex-wrap gap-1.5">
                      {seedRoleIds.map((rid) => {
                        const r = (roleList.data?.roles ?? []).find((x) => x.id === rid);
                        return (
                          <li
                            key={rid}
                            className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-2 py-0.5 text-xs"
                          >
                            <span>{r?.name ?? rid}</span>
                            <button
                              type="button"
                              onClick={() => toggleSeedRole(rid)}
                              className="ml-0.5 rounded p-0.5 hover:bg-muted"
                              aria-label={`Remove ${r?.name ?? rid}`}
                            >
                              <X className="h-3 w-3" aria-hidden="true" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <select
                    value=""
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v) toggleSeedRole(v);
                    }}
                    className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                    aria-label="Add a source role"
                  >
                    <option value="">Add a role…</option>
                    {(roleList.data?.roles ?? [])
                      .filter((r) => !seedRoleIds.includes(r.id))
                      .map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                          {r.is_system ? " (system)" : r.partner_name ? ` · ${r.partner_name}` : ""}
                        </option>
                      ))}
                  </select>
                  {seedDetailQueries.some((q) => q.isLoading) && (
                    <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" /> Loading source role grants…
                    </p>
                  )}
                  <p className="text-[11px] text-muted-foreground">
                    Pre-fill permissions from one or more existing roles. You can edit before saving
                    - the new role doesn&apos;t track changes to the source roles after creation.
                  </p>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Permission grid */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold tracking-tight">Permissions</h3>
          <p className="text-[11px] text-muted-foreground">{selected.size} selected</p>
        </div>
        {permissions.isLoading ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading permissions…
          </p>
        ) : permissions.isError ? (
          <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
            Failed to load permission catalog: {describe(permissions.error)}
          </div>
        ) : (
          <div className="space-y-3">
            {grouped.map((g) => (
              <div key={g.category} className="overflow-hidden rounded-lg border bg-card">
                <header className="border-b bg-muted/30 px-4 py-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                    {g.category}
                  </h4>
                </header>
                <ul className="divide-y">
                  {g.permissions.map((p) => {
                    const checked = selected.has(p.code);
                    const currentScope = selected.get(p.code) ?? null;
                    return (
                      <li key={p.code} className="flex items-start gap-3 px-4 py-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(p)}
                          className="mt-1 h-4 w-4 shrink-0"
                          aria-label={`Toggle ${p.code}`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-mono text-xs text-strong">{p.code}</p>
                          <p className="text-xs text-muted-foreground">{p.description}</p>
                        </div>
                        {p.supports_scope && (
                          <select
                            disabled={!checked}
                            value={currentScope ?? p.default_scope ?? ""}
                            onChange={(e) => changeScope(p.code, e.target.value)}
                            className={cn(
                              "ml-2 rounded-md border bg-background px-2 py-1 text-xs",
                              !checked && "opacity-40",
                            )}
                            aria-label={`Scope for ${p.code}`}
                          >
                            {p.valid_scopes.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ------------------------------------------------------------------- //
// Helpers
// ------------------------------------------------------------------- //

interface CategoryGroup {
  readonly category: string;
  readonly permissions: readonly PermissionDef[];
}

function groupByCategory(perms: readonly PermissionDef[]): readonly CategoryGroup[] {
  const map = new Map<string, PermissionDef[]>();
  for (const p of perms) {
    const list = map.get(p.category);
    if (list) {
      list.push(p);
    } else {
      map.set(p.category, [p]);
    }
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, list]) => ({
      category,
      permissions: list.slice().sort((a, b) => a.code.localeCompare(b.code)),
    }));
}

function serialiseGrants(selected: Map<string, string | null>): RoleGrantInput[] {
  const out: RoleGrantInput[] = [];
  for (const [code, scope] of selected.entries()) {
    out.push({ permission_code: code, scope });
  }
  return out;
}
